export const _forceRefresh = true;

import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { IAddress } from "models/address/address.model";
import {
  ICreateShipmentSlice,
  SetDropOffPayload,
  CreateShipmentAddress,
  CreateAddress,
  CreateShipmentContents,
  CreateShipmentAddressType,
  FoundDropOffLocationsDistance,
  FoundDropOffLocations,
  ExtraServicesType,
  DHLServicePoint,
  DropOffAddress,
  DropOffAddressWithServiceId,
} from "./createShipmentSlice.types";
import {
  DEFAULT_FROM_COUNTRY_ISO_CODE,
  DEFAULT_PACKAGE,
  GA_DEFAULT_FROM_COUNTRY_ISO_CODE,
} from "constants/misc";
import {
  isModalClosed,
  isModalOpened,
} from "store/modalSlice/modalSlice.actions";
import { modals } from "store/modalSlice/modalSlice.types";
import {
  IShipmentPackage,
  IShipmentPackageGrouped,
} from "models/shipmentPackage/shipmentPackage.model";
import {
  CreateShipmentSliceActions,
  isCountryChanged,
} from "./CreateShipmentSlice.actions.enum";
import {
  CreateShipmentInitialState,
  DEFAULT_ITEM_CONTENT,
  DEFAULT_ITEM_CONTENT_ERRORS,
  DEFAULT_ITEM_CONTENT_TOUCHED,
  DEFAULT_PACKAGE_ERRORS,
  DEFAULT_PACKAGE_TOUCHED,
  getDefaultPickupValues,
  DEFAULT_DROPOFF_VALUES,
} from "./CreateShipmentSliceDefaults.const";
import { isZipRequired } from "utils/zip/isZipRequired.util";
import { findIndex, findLastIndex } from "lodash";
import {
  ISalesAccountDefaults,
  ISalesAccount,
} from "models/salesAccount/salesAccount.model";
import {
  fetchShipmentDropOffLocations,
  fetchShipmentDropOffLocationsUPS,
} from "services/api/shipments";
import {
  fetchDHLPickupCapabilities,
  fetchDHLServicePoints,
} from "services/api/quotes";
import { IQuotedRate } from "models/quote/quote.model";
import {
  IPickupPoint,
  pickupPointFromStored,
} from "models/pickupPoint/pickupPoint.model";
import { AppThunk } from "store";

/*
Notes:

1. itd-staff account types will not have default settings loaded on the front end. Therefore, you must use the ? operator to handle null default settings.

*/

export const CREATE_SHIPMENT_SLICE_NAME = "createShipmentSlice";

const clearReceiverPickupPointAddress = (receiver: CreateShipmentAddress) => {
  receiver.pickupPoint = null;
  receiver.addressLine1 = "";
  receiver.addressLine2 = "";
  receiver.addressLine3 = "";
  receiver.city = "";
  receiver.countyState = "";
  receiver.zip = "";
};

const MAX_EMAILS = 5;
const norm = (s: string) => s.trim().toLowerCase();

export const createShipmentSlice = createSlice({
  name: CREATE_SHIPMENT_SLICE_NAME,
  initialState: CreateShipmentInitialState,
  reducers: {
    [CreateShipmentSliceActions.SET_TOTAL_SHIPMENT_VALUE]: (
      state,
      action: PayloadAction<number>
    ) => {
      //After entering a value, divide that value evenly into item values. Factor in package quantity, item quantity field, how many total packages, and total items.
      const totalShipmentMonetaryValue = action.payload;

      let totalNumberOfIndividualPhysicalItems = 0;
      state.values.contents.forEach((contentItem) => {
        // contentItem.packageId is the index of the package type in state.values.items
        // Ensure packageId is a valid index and refers to an existing package
        if (
          contentItem.packageId !== undefined &&
          contentItem.packageId >= 0 &&
          contentItem.packageId < state.values.items.length
        ) {
          const parentPackage = state.values.items[contentItem.packageId];
          if (
            parentPackage &&
            parentPackage.quantity > 0 &&
            contentItem.quantity > 0
          ) {
            // parentPackage.quantity is "how many physical packages of this type exist"
            // contentItem.quantity is "how many of this item type are in one package of its kind"
            totalNumberOfIndividualPhysicalItems +=
              contentItem.quantity * parentPackage.quantity;
          }
        }
      });

      if (totalNumberOfIndividualPhysicalItems === 0) {
        // Safely handle zero total individual physical items
        state.totalShipmentValue = action.payload; // Or perhaps action.payload if you want to preserve the input
        // Reset itemValues in contents if no effective items to distribute value to
        state.values.contents = state.values.contents.map((item) => ({
          ...item,
          itemValue: 0,
        }));
        // Mark as touched, consistent with original behavior
        state.touched.contents = state.touched.contents.map((touchedItem) => ({
          ...touchedItem,
          itemValue: true,
        }));
        return;
      }

      // Only calculates average item value if total shipment value is provided
      if (totalShipmentMonetaryValue) {
        // This is the monetary value for one single physical item
        const valuePerIndividualPhysicalItem =
          totalShipmentMonetaryValue / totalNumberOfIndividualPhysicalItems;

        state.values.contents = state.values.contents.map((item) => ({
          ...item,
          // itemValue should be the value of a single unit of this content type, in cents
          itemValue: Math.round(valuePerIndividualPhysicalItem * 100), // Use Math.round to avoid floating point issues for currency
        }));
      }

      state.touched.contents = state.touched.contents.map((touchedItem) => ({
        ...touchedItem,
        itemValue: true,
      }));

      state.totalShipmentValue = action.payload; // Set the total shipment value
    },

    [CreateShipmentSliceActions.SET_INSURANCE_CURRENCY]: (
      state,
      action: PayloadAction<string>
    ) => {
      state.insurance.insuranceAmountCurrency = action.payload;
    },

    [CreateShipmentSliceActions.SET_DEFAULT_FROM_ISO_GA]: (state) => {
      state.values.sender.countryIsoCode = GA_DEFAULT_FROM_COUNTRY_ISO_CODE;
      state.values.receiver.countryIsoCode = GA_DEFAULT_FROM_COUNTRY_ISO_CODE;
    },
    [CreateShipmentSliceActions.SET_INSURANCE_AMOUNT]: (
      state,
      action: PayloadAction<number>
    ) => {
      state.insurance.insuranceAmount = action.payload;
    },

    [CreateShipmentSliceActions.SET_USE_INSURANCE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload === false) {
        state.insurance.insuranceAmount = 0;
      }
      state.insurance.useInsurance = action.payload;
    },
    [CreateShipmentSliceActions.SET_REQUIRES_SIGNATURE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload === true) {
        state.requiresAdultSignature = false;
      }
      state.requiresSignature = action.payload;
    },

    [CreateShipmentSliceActions.SET_REQUIRES_ADULT_SIGNATURE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload === true) {
        state.requiresSignature = false;
      }
      state.requiresAdultSignature = action.payload;
    },

    [CreateShipmentSliceActions.SET_REQUIRES_DIRECT_DELIVERY]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.requiresDirectDelivery = action.payload;
    },
    [CreateShipmentSliceActions.SET_USED_IMPORT_ITEMS_AI_FEATURE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.usedImportItemsAiFeature = action.payload;
    },
    [CreateShipmentSliceActions.SET_REQUIRES_EMAIL_NOTIFICATION]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.requiresEmailNotification = action.payload;
    },

    [CreateShipmentSliceActions.SET_NOTIFICATION_EMAILS]: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const normalized: string[] = Array.from(
        new Set(action.payload.map(norm).filter(Boolean))
      ).slice(0, MAX_EMAILS);
      state.selectedNotificationEmails = normalized;

    },

    [CreateShipmentSliceActions.ADD_NOTIFICATION_EMAIL]: (
      state,
      action: PayloadAction<string>
    ) => {
      const v = norm(action.payload);
      if (!v) return;
      const existing = new Set(state.selectedNotificationEmails);
      existing.add(v);
      state.selectedNotificationEmails = Array.from(existing).slice(0, MAX_EMAILS);

    },
    [CreateShipmentSliceActions.REMOVE_NOTIFICATION_EMAIL]: (
      state,
      action: PayloadAction<string>
    ) => {
      const v = norm(action.payload);
      state.selectedNotificationEmails = state.selectedNotificationEmails.filter(
        (e) => e !== v
      );
    },
      [CreateShipmentSliceActions.CLEAR_NOTIFICATION_EMAILS]: ( state
    ) => {
       state.selectedNotificationEmails = [];
    },

    [CreateShipmentSliceActions.SET_EXTRA_SERVICE_OPTIONS]: (
      state,
      action: PayloadAction<ExtraServicesType>
    ) => {
      state.extraServices = action.payload;
    },

    [CreateShipmentSliceActions.SET_DHL_VALUE_ADDED_SERVICES]: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.dhlValueAddedServices = action.payload;
    },

    [CreateShipmentSliceActions.SET_DISCLAIMER_CHECKED]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isDisclaimerAccepted = action.payload;
    },

    [CreateShipmentSliceActions.SET_PICKUP_INSTRUCTIONS]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["pickUpObject"]["pickupInstructions"]
      >
    ) => {
      state.pickUpObject.pickupInstructions = action.payload;
    },
    [CreateShipmentSliceActions.SET_DHL_PICKUP_CAPABILITIES]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["pickUpObject"]["dhlPickupCapabilities"]
      >
    ) => {
      state.pickUpObject.dhlPickupCapabilities = action.payload;
    },
    [CreateShipmentSliceActions.SET_PICKUP_TO_TIME]: (
      state,
      action: PayloadAction<string>
    ) => {
      // dsv wants date in local time zone
      state.pickUpObject.pickupToTime = action.payload;
    },
    [CreateShipmentSliceActions.SET_PICKUP_FROM_TIME]: (
      state,
      action: PayloadAction<string>
    ) => {
      state.pickUpObject.pickupFromTime = action.payload;
    },
    [CreateShipmentSliceActions.SET_PICKUP]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["pickUpObject"]["shouldPickUp"]
      >
    ) => {
      state.pickUpObject.shouldPickUp = action.payload;
      if (action.payload && state.dropOffObject.shouldDropOff) {
        state.dropOffObject.shouldDropOff = false;
      }
    },
    [CreateShipmentSliceActions.SET_DROPOFF]: (
      state,
      action: PayloadAction<SetDropOffPayload>
    ) => {
      const { shouldDropOff } = action.payload;
      state.dropOffObject.shouldDropOff = shouldDropOff;

      if (shouldDropOff && state.pickUpObject.shouldPickUp) {
        state.pickUpObject.shouldPickUp = false;
      }
    },
    [CreateShipmentSliceActions.SET_SELECTED_LOCATION]: (
      state,
      action: PayloadAction<FoundDropOffLocations>
    ) => {
      state.dropOffObject.selectedDropOffLocation = action.payload;
    },
    [CreateShipmentSliceActions.SET_DROP_OFF_LOCATIONS]: (
      state,
      action: PayloadAction<FoundDropOffLocationsDistance[]>
    ) => {
      state.dropOffObject.dropOffLocations = action.payload;
    },
    [CreateShipmentSliceActions.SET_SENDING_TO_RESIDENCE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.shippingToResidence = action.payload;
    },
      [CreateShipmentSliceActions.SET_SUNDAY_DELIVERY]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.sundayDelivery = action.payload;
    },
      [CreateShipmentSliceActions.SET_SATURDAY_DELIVERY]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.saturdayDelivery = action.payload;
    },
    [CreateShipmentSliceActions.SET_PICKUP_DATE]: (
      state,
      action: PayloadAction<string>
    ) => {
      state.pickUpObject.pickupDate = action.payload;
      const newDate = action.payload.split("T")[0];
      const fromTime = state.pickUpObject.pickupFromTime.split("T")[1];
      const toTime = state.pickUpObject.pickupToTime.split("T")[1];
      state.pickUpObject.pickupFromTime = newDate + "T" + fromTime;
      state.pickUpObject.pickupToTime = newDate + "T" + toTime;
    },
    [CreateShipmentSliceActions.SET_SALES_ACCOUNT_UUID]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["salesAccountUUID"]>
    ) => {
      state.salesAccountUUID = action.payload;
    },

    [CreateShipmentSliceActions.SET_ERRORS]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["errors"]>
    ) => {
      state.errors = action.payload;
    },

    [CreateShipmentSliceActions.SET_SUBMIT_COUNT]: (state) => {
      state.submitCount = state.submitCount + 1;
    },

    [CreateShipmentSliceActions.SET_PRINT_LATER]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["printLater"]>
    ) => {
      state.printLater = action.payload;
    },

    [CreateShipmentSliceActions.SET_CONVERTING_FROM_QUOTE_STATUS]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["convertingFromQuoteStatus"]>
    ) => {
      state.convertingFromQuoteStatus = action.payload;
    },

    [CreateShipmentSliceActions.SET_IS_CONVERTING_FROM_QUOTE]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["isConvertingFromQuote"]>
    ) => {
      state.isConvertingFromQuote = action.payload;
    },

    [CreateShipmentSliceActions.SET_VALUES]: (
      state,
      action: PayloadAction<
        Pick<
          ICreateShipmentSlice["values"],
          "receiver" | "sender" | "importer" | "items" | "details" | "contents"
        >
      >
    ) => {
      state.values = {
        ...state.values,
        ...action.payload,
      };
    },

    [CreateShipmentSliceActions.SET_SENDER_SAVE_TO_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["sender"]["saveToAddressBook"]
      >
    ) => {
      state.values.sender.saveToAddressBook = action.payload;
    },

    [CreateShipmentSliceActions.SET_RECIPIENT_SAVE_TO_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["receiver"]["saveToAddressBook"]
      >
    ) => {
      state.values.receiver.saveToAddressBook = action.payload;
    },
    [CreateShipmentSliceActions.SET_IMPORTER_SAVE_TO_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["importer"]["saveToAddressBook"]
      >
    ) => {
      state.values.importer.saveToAddressBook = action.payload;
    },

    [CreateShipmentSliceActions.SET_SENDER_UPDATE_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["sender"]["updateAddressBook"]
      >
    ) => {
      state.values.sender.updateAddressBook = action.payload;
    },

    [CreateShipmentSliceActions.SET_RECIPIENT_UPDATE_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["receiver"]["updateAddressBook"]
      >
    ) => {
      state.values.receiver.updateAddressBook = action.payload;
    },

    [CreateShipmentSliceActions.SET_IMPORTER_UPDATE_ADDRESS_BOOK]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["importer"]["updateAddressBook"]
      >
    ) => {
      state.values.importer.updateAddressBook = action.payload;
    },

    [CreateShipmentSliceActions.SET_EDITING_ID]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["editingShipmentID"]>
    ) => {
      state.editingShipmentID = action.payload;
    },

    [CreateShipmentSliceActions.SET_EDITING_UUID]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["editingShipmentUUID"]>
    ) => {
      state.editingShipmentUUID = action.payload;
    },

    [CreateShipmentSliceActions.SET_EDITING_JOB_NUMBER]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["editingShipmentJobNumber"]>
    ) => {
      state.editingShipmentJobNumber = action.payload;
    },

    [CreateShipmentSliceActions.SET_SELECTED_RATE]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["selectedRate"]>
    ) => {
      if (!action.payload) {
        // reset pick up and insurance if deselected a service
        state.pickUpObject = getDefaultPickupValues();
        state.dropOffObject = DEFAULT_DROPOFF_VALUES;
        state.insurance = CreateShipmentInitialState.insurance;
      }
      state.selectedRate = action.payload;
    },

    [CreateShipmentSliceActions.SET_RATES_LOADING_STATUS]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["ratesLoadingStatus"]>
    ) => {
      state.ratesLoadingStatus = action.payload;
    },

    [CreateShipmentSliceActions.SET_SUBMITTING_STATUS]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["submittingStatus"]>
    ) => {
      state.submittingStatus = action.payload;
    },

    [CreateShipmentSliceActions.SET_SUBMITTING_ERROR_MESSAGE]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["submittingErrorMessage"]>
    ) => {
      state.submittingErrorMessage = action.payload;
    },

    [CreateShipmentSliceActions.SET_FORM_TYPE]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["type"]>
    ) => {
      state.type = action.payload;
    },

    [CreateShipmentSliceActions.SET_RATES_ERROR]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["ratesError"]>
    ) => {
      state.ratesError = action.payload;
    },

    [CreateShipmentSliceActions.SET_SENDER_SAVED_ADDRESS]: (
      state,
      action: PayloadAction<
        // Senders are never lockers, and address book entries store a locker in a
        // smaller shape than the form, so pickupPoint is left out.
        Omit<CreateShipmentAddress, "type" | "saveToAddressBook" | "pickupPoint">
      >
    ) => {
      state.values.sender.id = action.payload.id;
      state.values.sender.uuid = action.payload.uuid;
      state.values.sender.firstName = action.payload.firstName;
      state.values.sender.lastName = action.payload.lastName;
      state.values.sender.addressLine1 = action.payload.addressLine1;
      state.values.sender.addressLine2 = action.payload.addressLine2;
      state.values.sender.addressLine3 = action.payload.addressLine3;
      state.values.sender.city = action.payload.city;
      state.values.sender.countyState = action.payload.countyState;
      state.values.sender.countryIsoCode = action.payload.countryIsoCode;
      state.values.sender.zip = action.payload.zip;
      state.values.sender.phone = action.payload.phone;
      state.values.sender.email = action.payload.email;
      state.values.sender.company = action.payload.company;
      state.values.sender.eoriNumber = action.payload.eoriNumber;
      state.values.sender.vatNumber = action.payload.vatNumber;
      state.values.sender.isSenderDefault = action.payload.isSenderDefault;
      state.values.sender.saveToAddressBook = false;
      state.values.sender.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.SET_DEEFAULT_FROM_ADDRESS]: (
      state,
      action: PayloadAction<
        Omit<CreateShipmentAddress, "type" | "saveToAddressBook" | "pickupPoint">
      >
    ) => {
      state.values.sender.id = action.payload.id;
      state.values.sender.uuid = action.payload.uuid;
      state.values.sender.isSenderDefault = action.payload.isSenderDefault;
      state.values.sender.firstName = action.payload.firstName;
      state.values.sender.lastName = action.payload.lastName;
      state.values.sender.addressLine1 = action.payload.addressLine1;
      state.values.sender.addressLine2 = action.payload.addressLine2;
      state.values.sender.addressLine3 = action.payload.addressLine3;
      state.values.sender.city = action.payload.city;
      state.values.sender.countyState = action.payload.countyState;
      state.values.sender.countryIsoCode = action.payload.countryIsoCode;
      state.values.sender.zip = action.payload.zip;
      state.values.sender.phone = action.payload.phone;
      state.values.sender.email = action.payload.email;
      state.values.sender.company = action.payload.company;
      state.values.sender.eoriNumber = action.payload.eoriNumber;
      state.values.sender.vatNumber = action.payload.vatNumber;
    },

    [CreateShipmentSliceActions.ADD_CONTENTS_ROW]: (
      state,
      action: PayloadAction<{
        packageId: number;
        defaultSettings: ISalesAccountDefaults;
      }>
    ) => {
      const newItemRow = {
        ...DEFAULT_ITEM_CONTENT,
        packageId: action.payload.packageId,
      };

      if (action.payload.defaultSettings?.countryOfOrigin) {
        newItemRow.countryOfOrigin =
          action.payload.defaultSettings.countryOfOrigin;
      }

      if (action.payload.defaultSettings?.manufacturerDetails) {
        newItemRow.manufacturerDetails =
          action.payload.defaultSettings.manufacturerDetails;
      }

      if (action.payload.defaultSettings?.itemsCurrency) {
        newItemRow.currency = action.payload.defaultSettings.itemsCurrency;
      }

      state.values.contents = [...state.values.contents, newItemRow];

      state.errors.contents = [
        ...state.errors.contents,
        { ...DEFAULT_ITEM_CONTENT_ERRORS },
      ];

      state.touched.contents = [
        ...state.touched.contents,
        { ...DEFAULT_ITEM_CONTENT_TOUCHED },
      ];
    },

    [CreateShipmentSliceActions.REMOVE_CONTENTS_ROW]: (
      state,
      action: PayloadAction<number>
    ) => {
      state.values.contents = current(state.values.contents).filter(
        (item, index) => index !== action.payload
      );

      state.touched.contents = current(state.touched.contents).filter(
        (item, index) => index !== action.payload
      );

      state.errors.contents = current(state.errors.contents).filter(
        (item, index) => index !== action.payload
      );
    },

    [CreateShipmentSliceActions.REPLACE_PACKAGE_CONTENTS]: (
      state,
      action: PayloadAction<{
        packageId: number;
        contents: CreateShipmentContents[];
      }>
    ) => {
      const { packageId, contents } = action.payload;

      // Keep every other package's contents (and their aligned touched/errors)
      // in place, then append the freshly-extracted rows for this package.
      const keptContents: CreateShipmentContents[] = [];
      const keptTouched: typeof state.touched.contents = [];
      const keptErrors: typeof state.errors.contents = [];

      current(state.values.contents).forEach((content, index) => {
        if (content.packageId !== packageId) {
          keptContents.push(content);
          keptTouched.push(state.touched.contents[index]);
          keptErrors.push(state.errors.contents[index]);
        }
      });

      const newContents = contents.map((content) => ({
        ...content,
        packageId,
      }));

      state.values.contents = [...keptContents, ...newContents];
      state.touched.contents = [
        ...keptTouched,
        ...newContents.map(() => ({ ...DEFAULT_ITEM_CONTENT_TOUCHED })),
      ];
      state.errors.contents = [
        ...keptErrors,
        ...newContents.map(() => ({ ...DEFAULT_ITEM_CONTENT_ERRORS })),
      ];

      // Set this package's weight to the sum of the uploaded item weights.
      // Item weight and package weight share the same DB units, so summing
      // (per-unit weight * item quantity) gives the package weight.
      const pkg = state.values.items[packageId];
      if (pkg) {
        const totalItemWeight = newContents.reduce(
          (sum, content) =>
            sum + (content.weight || 0) * (content.quantity || 0),
          0,
        );
        state.values.items[packageId] = {
          ...pkg,
          weight: totalItemWeight,
        };
      }
    },

    [CreateShipmentSliceActions.SET_SENDER_ADDRESS_TYPE]: (
      state,
      action: PayloadAction<CreateShipmentAddressType>
    ) => {
      state.values.sender.type = action.payload;
      state.values.sender.editModeSavedAddress = false;
      state.values.sender.saveToAddressBook = false;
      state.values.sender.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.SET_SAVED_SENDER_ADDRESS_EDIT_MODE]: (
      state
    ) => {
      state.values.sender.editModeSavedAddress =
        !state.values.sender.editModeSavedAddress;
    },

    [CreateShipmentSliceActions.SET_SAVED_RECEIVER_ADDRESS_EDIT_MODE]: (
      state
    ) => {
      state.values.receiver.editModeSavedAddress =
        !state.values.receiver.editModeSavedAddress;
    },

    [CreateShipmentSliceActions.SET_SAVED_IMPORTER_ADDRESS_EDIT_MODE]: (
      state
    ) => {
      state.values.importer.editModeSavedAddress =
        !state.values.importer.editModeSavedAddress;
    },
    [CreateShipmentSliceActions.SET_IMPORTER_SWITCH]: (state) => {
      state.values.importer.id = null;
      state.values.details.setImporterSwitch = !state.values.details.setImporterSwitch;
    },

    [CreateShipmentSliceActions.CLEAR_SENDER_ADDRESS]: (state) => {
      state.values.sender.id = null;
      state.values.sender.uuid = null;
      state.values.sender.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.CLEAR_RECEIVER_ADDRESS]: (state) => {
      state.values.receiver.id = null;
      state.values.receiver.uuid = null;
      state.values.receiver.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.CLEAR_IMPORTER_ADDRESS]: (state) => {
      state.values.importer.id = null;
      state.values.importer.uuid = null;
      state.values.importer.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.ON_TOUCH_ITEM_FIELD]: (
      state,
      action: PayloadAction<{ id: number; key: keyof IShipmentPackageGrouped }>
    ) => {
      const clonedItems = [...state.touched.items];

      const item = clonedItems[action.payload.id];

      if (item && item[action.payload.key]) item[action.payload.key] = true;

      state.touched.items = clonedItems;
    },

    [CreateShipmentSliceActions.SET_RECEIVER_ADDRESS_TYPE]: (
      state,
      action: PayloadAction<CreateShipmentAddressType>
    ) => {
      // Leaving the locker option: the address fields hold the locker's
      // address, which must not become a home delivery address.
      if (
        action.payload !== CreateShipmentAddressType.PICKUP_POINT &&
        state.values.receiver.pickupPoint
      ) {
        clearReceiverPickupPointAddress(state.values.receiver);
      }
      state.values.receiver.type = action.payload;
      state.values.receiver.editModeSavedAddress = false;
      state.values.receiver.saveToAddressBook = false;
      state.values.receiver.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.SET_RECEIVER_PICKUP_POINT]: (
      state,
      action: PayloadAction<IPickupPoint | null>
    ) => {
      const receiver = state.values.receiver;
      const point = action.payload;

      if (!point) {
        clearReceiverPickupPointAddress(receiver);
        return;
      }

      // The receiver keeps their name, email and mobile; the address becomes
      // the locker's, so it shows on the shipment and decides the rates.
      receiver.type = CreateShipmentAddressType.PICKUP_POINT;
      receiver.pickupPoint = point;
      receiver.id = null;
      receiver.uuid = null;
      receiver.saveToAddressBook = false;
      receiver.updateAddressBook = false;
      receiver.editModeSavedAddress = false;
      receiver.company = "";
      receiver.addressLine1 = point.name;
      receiver.addressLine2 = point.addressLine1;
      receiver.addressLine3 = "";
      receiver.city = point.city;
      receiver.countyState = "";
      receiver.zip = point.zip;
      receiver.countryIsoCode = point.countryIsoCode;
      receiver.residentialAddress = false;
    },

    [CreateShipmentSliceActions.SET_IMPORTER_ADDRESS_TYPE]: (
      state,
      action: PayloadAction<CreateShipmentAddressType>
    ) => {
      state.values.importer.type = action.payload;
      state.values.importer.editModeSavedAddress = false;
      state.values.importer.saveToAddressBook = false;
      state.values.importer.updateAddressBook = false;
    },

    [CreateShipmentSliceActions.SET_RECEIVER_SAVED_ADDRESS]: (
      state,
      action: PayloadAction<IAddress>
    ) => {
      state.values.receiver.id = action.payload.id;
      state.values.receiver.uuid = action.payload.uuid;
      state.values.receiver.firstName = action.payload.firstName;
      state.values.receiver.lastName = action.payload.lastName;
      state.values.receiver.addressLine1 = action.payload.addressLine1;
      state.values.receiver.addressLine2 = action.payload.addressLine2;
      state.values.receiver.addressLine3 = action.payload.addressLine3;
      state.values.receiver.city = action.payload.city;
      state.values.receiver.countyState = action.payload.countyState;
      state.values.receiver.zip = action.payload.zip;
      state.values.receiver.countryIsoCode = action.payload.countryIsoCode;
      state.values.receiver.phone = action.payload.phone;
      state.values.receiver.email = action.payload.email;
      state.values.receiver.company = action.payload.company;
      state.values.receiver.eoriNumber = action.payload.eoriNumber;
      state.values.receiver.vatNumber = action.payload.vatNumber;
      state.values.receiver.residentialAddress = action.payload.residentialAddress;
      state.values.receiver.saveToAddressBook = false;
      state.values.receiver.updateAddressBook = false;

      // A saved locker or shop reopens the locker option with that point. Its
      // id and uuid stay set, so the locker option does not offer to save it again.
      const savedPoint = action.payload.pickupPoint;
      if (savedPoint?.id) {
        const receiver = state.values.receiver;
        receiver.type = CreateShipmentAddressType.PICKUP_POINT;
        receiver.pickupPoint = pickupPointFromStored(savedPoint, {
          line1: action.payload.addressLine1,
          line2: action.payload.addressLine2,
          city: action.payload.city,
          zip: action.payload.zip,
          countryIsoCode: action.payload.countryIsoCode,
        });
        receiver.editModeSavedAddress = false;
        receiver.company = "";
        receiver.residentialAddress = false;
      }
    },
    
    [CreateShipmentSliceActions.SET_IMPORTER_SAVED_ADDRESS]: (
      state,
      action: PayloadAction<IAddress>
    ) => {
      state.values.importer.id = action.payload.id;
      state.values.importer.uuid = action.payload.uuid;
      state.values.importer.firstName = action.payload.firstName;
      state.values.importer.lastName = action.payload.lastName;
      state.values.importer.addressLine1 = action.payload.addressLine1;
      state.values.importer.addressLine2 = action.payload.addressLine2;
      state.values.importer.addressLine3 = action.payload.addressLine3;
      state.values.importer.city = action.payload.city;
      state.values.importer.countyState = action.payload.countyState;
      state.values.importer.zip = action.payload.zip;
      state.values.importer.countryIsoCode = action.payload.countryIsoCode;
      state.values.importer.phone = action.payload.phone;
      state.values.importer.email = action.payload.email;
      state.values.importer.company = action.payload.company;
      state.values.importer.eoriNumber = action.payload.eoriNumber;
      state.values.importer.vatNumber = action.payload.vatNumber;
      state.values.importer.saveToAddressBook = false;
      state.values.importer.updateAddressBook = false;
    },
    [CreateShipmentSliceActions.ON_CHANGE_SENDER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<Partial<CreateShipmentAddress>>
    ) => {
      state.values.sender = { ...state.values.sender, ...action.payload };
    },

    [CreateShipmentSliceActions.SET_RECIPIENT_ADDRESS_ZIP]: (
      state,
      action: PayloadAction<CreateShipmentAddress["zip"]>
    ) => {
      state.values.receiver = { ...state.values.receiver, zip: action.payload };
    },

    [CreateShipmentSliceActions.SET_IMPORTER_ADDRESS_ZIP]: (
      state,
      action: PayloadAction<CreateShipmentAddress["zip"]>
    ) => {
      state.values.importer = { ...state.values.importer, zip: action.payload };
    },

    [CreateShipmentSliceActions.SET_SENDER_ADDRESS_ZIP]: (
      state,
      action: PayloadAction<CreateShipmentAddress["zip"]>
    ) => {
      state.values.sender = { ...state.values.sender, zip: action.payload };
    },

    [CreateShipmentSliceActions.ON_CHANGE_SENDER_COUNTRY]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["sender"]["countryIsoCode"]
      >
    ) => {
      state.values.sender.countryIsoCode = action.payload;
      state.values.sender.countyState = "";
      state.values.sender.zip = "";
    },

    [CreateShipmentSliceActions.ON_TOUCH_SENDER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<Partial<keyof CreateAddress>>
    ) => {
      state.touched.sender = {
        ...state.touched.sender,
        [action.payload]: true,
      };
    },

    [CreateShipmentSliceActions.ON_CHANGE_RECEIVER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<Partial<CreateShipmentAddress>>
    ) => {
      state.values.receiver = { ...state.values.receiver, ...action.payload };
    },
    [CreateShipmentSliceActions.ON_CHANGE_IMPORTER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<Partial<CreateShipmentAddress>>
    ) => {
      state.values.importer = { ...state.values.importer, ...action.payload };
    },

    [CreateShipmentSliceActions.ON_CHANGE_RECEIVER_COUNTRY]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["receiver"]["countryIsoCode"]
      >
    ) => {
      state.values.receiver.countryIsoCode = action.payload;
      state.values.receiver.countyState = "";
      state.values.receiver.zip = "";
    },
    [CreateShipmentSliceActions.ON_CHANGE_IMPORTER_COUNTRY]: (
      state,
      action: PayloadAction<
        ICreateShipmentSlice["values"]["receiver"]["countryIsoCode"]
      >
    ) => {
      state.values.importer.countryIsoCode = action.payload;
      state.values.importer.countyState = "";
      state.values.importer.zip = "";
    },

    [CreateShipmentSliceActions.ON_TOUCH_RECEIVER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<keyof CreateAddress>
    ) => {
      state.touched.receiver = {
        ...state.touched.receiver,
        [action.payload]: true,
      };
    },

    [CreateShipmentSliceActions.ON_TOUCH_IMPORTER_ADDRESS_FIELD]: (
      state,
      action: PayloadAction<keyof CreateAddress>
    ) => {
      state.touched.importer = {
        ...state.touched.importer,
        [action.payload]: true,
      };
    },

    [CreateShipmentSliceActions.ON_CHANGE_DETAILS_FIELD]: (
      state,
      action: PayloadAction<Partial<ICreateShipmentSlice["values"]["details"]>>
    ) => {
      state.values.details = { ...state.values.details, ...action.payload };
    },

    [CreateShipmentSliceActions.ADD_PACKAGE]: (
      state,
      action: PayloadAction<ISalesAccountDefaults>
    ) => {
      const defaultSettings = action.payload;
      state.values.items = [
        ...state.values.items,
        {
          ...DEFAULT_PACKAGE,
          ...(defaultSettings?.useDefaultPackageDimensionsInUi && {
            height: defaultSettings.height || 0,
            length: defaultSettings.length || 0,
            width: defaultSettings.width || 0,
          }),
          // Selecting a dangerous goods category marks every package as
          // restricted; keep packages added afterwards consistent with that
          // (FedEx rejects a booking that mixes DG and non-DG packages).
          dgPackage: Boolean(state.dangerousGoodsOption),
        },
      ];
      state.touched.items = [
        ...state.touched.items,
        {
          ...DEFAULT_PACKAGE_TOUCHED,
        },
      ];
      state.errors.items = [
        ...state.errors.items,
        {
          ...DEFAULT_PACKAGE_ERRORS,
        },
      ];
      // use standard values
      const content = {
        ...DEFAULT_ITEM_CONTENT,
        packageId: state.values.items.length - 1,
      };
      if (action.payload?.countryOfOrigin) {
        content.countryOfOrigin = action.payload.countryOfOrigin;
      }
      if (action.payload?.itemsCurrency) {
        content.currency = action.payload.itemsCurrency;
      }
      if (action.payload?.manufacturerDetails) {
        content.manufacturerDetails = action.payload.manufacturerDetails;
      }
      state.values.contents = [...state.values.contents, content];
      state.errors.contents = [
        ...state.errors.contents,
        {
          ...DEFAULT_ITEM_CONTENT_ERRORS,
        },
      ];
      state.touched.contents = [
        ...state.touched.contents,
        {
          ...DEFAULT_ITEM_CONTENT_TOUCHED,
        },
      ];
      // set default values for package dimensions
      // if (defaultSettings?.useDefaultPackageDimensionsInUi) {
      // state.values.items = state.values.items.map((item) => ({
      //   ...item,
      //   height: defaultSettings.height || 0,
      //   length: defaultSettings.length || 0,
      //   width: defaultSettings.width || 0,
      // }));
      // }
    },
    // Shipment-level entry methods keep their contents outside the packages,
    // so a new package gets no contents row.
    [CreateShipmentSliceActions.ADD_PACKAGE_WITHOUT_ITEM]: (
      state,
      action: PayloadAction<ISalesAccountDefaults>
    ) => {
      const defaultSettings = action.payload;
      state.values.items = [
        ...state.values.items,
        {
          ...DEFAULT_PACKAGE,
          ...(defaultSettings?.useDefaultPackageDimensionsInUi && {
            height: defaultSettings.height || 0,
            length: defaultSettings.length || 0,
            width: defaultSettings.width || 0,
          }),
          // Selecting a dangerous goods category marks every package as
          // restricted; keep packages added afterwards consistent with that
          // (FedEx rejects a booking that mixes DG and non-DG packages).
          dgPackage: Boolean(state.dangerousGoodsOption),
        },
      ];
      state.touched.items = [
        ...state.touched.items,
        {
          ...DEFAULT_PACKAGE_TOUCHED,
        },
      ];
      state.errors.items = [
        ...state.errors.items,
        {
          ...DEFAULT_PACKAGE_ERRORS,
        },
      ];
    },

    [CreateShipmentSliceActions.SET_PACKAGES]: (
      state,
      action: PayloadAction<IShipmentPackageGrouped[]>
    ) => {
      state.values.items = action.payload;
    },

    [CreateShipmentSliceActions.SET_ITEMS]: (
      state,
      action: PayloadAction<CreateShipmentContents[]>
    ) => {
      state.values.contents = action.payload;
    },

    [CreateShipmentSliceActions.SET_RATES]: (
      state,
      action: PayloadAction<ICreateShipmentSlice["rates"]>
    ) => {
      // order rates by name
      action.payload.sort((a, b) => {
        if (a.carrier.name < b.carrier.name) return -1;
        if (a.carrier.name > b.carrier.name) return 1;
        return 0;
      });
      state.rates = action.payload;
    },

    [CreateShipmentSliceActions.REMOVE_PACKAGE]: (
      state,
      action: PayloadAction<number>
    ) => {
      const findFirstContentsForPackage = findIndex(
        state.values.contents,
        (item) => item.packageId === action.payload
      );

      const findLastContentsForPackage = findLastIndex(
        state.values.contents,
        (item) => item.packageId === action.payload
      );

      state.touched.contents = state.touched.contents.splice(
        findFirstContentsForPackage,
        findLastContentsForPackage
      );

      state.errors.contents = state.errors.contents.splice(
        findFirstContentsForPackage,
        findLastContentsForPackage
      );

      state.values.contents = state.values.contents.filter(
        (content) => content.packageId !== action.payload
      );

      state.values.items = state.values.items.filter(
        (item, index) => index !== action.payload
      );

      state.touched.items = state.touched.items.filter(
        (item, index) => index !== action.payload
      );

      state.errors.items = state.errors.items.filter(
        (item, index) => index !== action.payload
      );
    },

    [CreateShipmentSliceActions.UPDATE_PACKAGE_VALUE]: (
      state,
      action: PayloadAction<{
        id: number;
        value: string | number;
        field: keyof IShipmentPackage;
      }>
    ) => {
      const { id, value, field } = action.payload;

      const p = state.values.items[id];

      if (p) {
        state.values.items[id] = { ...p, [field]: value };
      }
    },

    [CreateShipmentSliceActions.ON_CHANGE_CONTENTS_FIELD]: (
      state,
      action: PayloadAction<{
        id: number;
        value: string | number;
        field: keyof CreateShipmentContents;
      }>
    ) => {
      const { id, value, field } = action.payload;

      const item = state.values.contents[id];

      if (item) {
        state.values.contents[id] = { ...item, [field]: value };
      }
    },

    [CreateShipmentSliceActions.ON_TOUCH_CONTENTS_FIELD]: (
      state,
      action: PayloadAction<{ id: number; key: keyof CreateShipmentContents }>
    ) => {
      const clonedItems = [...state.touched.contents];

      const item = clonedItems[action.payload.id];

      if (item && item[action.payload.key]) item[action.payload.key] = true; //https://app.bugsnag.com/itd-global/connexx-frontend/errors/65659e62cc3ef90008eb42f1?event_id=6566ef5300cdc4c788360000&i=sk&m=oc

      state.touched.contents = clonedItems;
    },

    [CreateShipmentSliceActions.SET_DEFAULT_PACKAGE_DIMENSIONS]: (
      state,
      action: PayloadAction<{ defaultSettings: ISalesAccountDefaults }>
    ) => {
      const defaultSettings = action.payload.defaultSettings;
      if (
        defaultSettings?.useDefaultPackageDimensionsInUi &&
        state.values.items.length === 1
      ) {
        state.values.items = state.values.items.map((item) => ({
          ...item,
          height: defaultSettings.height || 0,
          length: defaultSettings.length || 0,
          width: defaultSettings.width || 0,
        }));
      }
    },

    [CreateShipmentSliceActions.SET_DEFAULT_ITEM_VALUE_INITIAL_STATE]: (
      state,
      action: PayloadAction<{ defaultSettings: ISalesAccountDefaults }>
    ) => {
      if (!state.editingShipmentJobNumber) {
        // don't set default values if editing shipment
        if (action.payload?.defaultSettings?.countryOfOrigin) {
          state.values.contents[0].countryOfOrigin =
            action.payload.defaultSettings.countryOfOrigin;
        }

        if (action.payload.defaultSettings?.commodityCode) {
          state.values.contents[0].commodityCode =
            action.payload.defaultSettings.commodityCode;
        }

        if (action.payload.defaultSettings?.manufacturerDetails) {
          state.values.contents[0].manufacturerDetails =
            action.payload.defaultSettings.manufacturerDetails;
        }

        if (action.payload.defaultSettings?.itemDescription) {
          state.values.contents[0].description =
            action.payload.defaultSettings.itemDescription;
        }

        if (action.payload?.defaultSettings?.itemsCurrency) {
          state.values.contents[0].currency =
            action.payload.defaultSettings.itemsCurrency;

          state.insurance.insuranceAmountCurrency =
            action.payload.defaultSettings.itemsCurrency;
        }
      }
    },

    [CreateShipmentSliceActions.SET_FILES]: (
      state,
      action: PayloadAction<
        { fileType: string; label: string; file: File | null }[]
      >
    ) => {
      state.files = action.payload;
    },

    [CreateShipmentSliceActions.SET_CURRENT_QUOTE_REFERENCE]: (
      state,
      action: PayloadAction<string>
    ) => {
      state.currentQuoteReference = action.payload;
    },

    [CreateShipmentSliceActions.SET_SEND_INTEGRATION_UPDATE]: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.sendIntegrationUpdate = action.payload;
    },

    [CreateShipmentSliceActions.SET_SHIPMENT_DATE_FROM_UI]: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.shipmentDateFromUI = action.payload;
    },

    setDocumentOption: (
      state,
      action: PayloadAction<{ label: string; value: number }>
    ) => {
      state.documentOption = action.payload;
    },

    setDangerousGoodsOption: (
      state,
      action: PayloadAction<{ label: string; value: number }>
    ) => {
      state.dangerousGoodsOption = action.payload;
    },

    setDuplicateOrEdit: (state, action: PayloadAction<string>) => {
      state.duplicateOrEdit = action.payload;
    },

    setEditingOriginalReferences: (
      state,
      action: PayloadAction<ICreateShipmentSlice["editingOriginalReferences"]>
    ) => {
      state.editingOriginalReferences = action.payload;
    },

    setKeptDocumentTypes: (
      state,
      action: PayloadAction<ICreateShipmentSlice["keptDocumentTypes"]>
    ) => {
      state.keptDocumentTypes = action.payload;
    },

    setCurrentQuoteReferenceIsValid: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.currentQuoteReferenceIsValid = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      isModalClosed,
      (state, action: { payload: { modalName: modals } }) => {
        if (action.payload.modalName === "createShipment") {
          Object.assign(state, CreateShipmentInitialState);
        }
      }
    );

    builder.addMatcher(
      isModalOpened,
      (state, action: { payload: { name: modals } }) => {
        if (action.payload.name === "createShipment") {
          state.values.sender.countryIsoCode = DEFAULT_FROM_COUNTRY_ISO_CODE;
          state.values.receiver.countryIsoCode = DEFAULT_FROM_COUNTRY_ISO_CODE;
          state.pickUpObject = getDefaultPickupValues();
          state.dropOffObject = DEFAULT_DROPOFF_VALUES;
        }
      }
    );

    builder.addMatcher(isCountryChanged, (state) => {
      if (
        state.errors.sender.zip &&
        !isZipRequired(state.values.sender.countryIsoCode)
      ) {
        state.errors.sender.zip = "";
      }

      if (
        state.errors.receiver.zip &&
        !isZipRequired(state.values.receiver.countryIsoCode)
      ) {
        state.errors.receiver.zip = "";
      }
    });
  },
});

export const {
  addPackage,
  addPackageWithoutItem,
  addContentsRow,
  removeContentsRow,
  replacePackageContents,
  setIsConvertingFromQuote,
  removePackage,
  setEditingID,
  setEditingUUID,
  setRatesLoadingStatus,
  setSalesAccountUUID,
  setSelectedRate,
  setSenderAddressType,
  setSenderSaveToAddressBook,
  setSubmittingErrorMessage,
  setSubmittingStatus,
  setSubmitCount,
  setErrors,
  setRatesError,
  setRecipientSaveToAddressBook,
  setImporterSaveToAddressBook,
  setSenderUpdateAddressBook,
  setRecipientUpdateAddressBook,
  setImporterUpdateAddressBook,
  clearSenderAddress,
  clearReceiverAddress,
  clearImporterAddress,
  setConvertingFromQuoteStatus,
  setPrintLater,
  setRates,
  setFormType,
  setPackages,
  setItems,
  setEditingJobNumber,
  setValues,
  setRecipientAddressZip,
  setImporterAddressZip,
  setSenderAddressZip,
  setReceiverAddressType,
  setReceiverPickupPoint,
  setImporterAddressType,
  setReceiverSavedAddress,
  setImporterSavedAddress,
  onChangeReceiverAddressField,
  onChangeImporterAddressField,
  onChangeReceiverCountry,
  onChangeImporterCountry,
  onChangeSenderCountry,
  setSenderSavedAddress,
  onTouchItemField,
  onTouchReceiverAddressField,
  onTouchImporterAddressField,
  onTouchSenderAddressField,
  onTouchContentsField,
  onChangeSenderAddressField,
  onChangeContentsField,
  onChangeDetailsField,
  updatePackageValue,
  setPickUp,
  setSaturdayDelivery,
  setSundayDelivery,
  setDropOff,
  setSelectedLocation,
  setDropOffLocations,
  setSendingToResidence,
  setPickUpDate,
  setPickUpFromTime,
  setPickUpToTime,
  setPickUpInstructions,
  setDHLPickupCapabilities,
  setDefaultItemValueInitialState,
  setDefaultFromISO_GA,
  setFiles,
  setSavedReceiverAddressEditMode,
  setSavedImporterAddressEditMode,
  setImporterSwitch,
  setSavedSenderAddressEditMode,
  setDocumentOption,
  setDangerousGoodsOption,
  setCurrentQuoteReference,
  setDisclaimerChecked,
  setUseInsurance,
  setRequiresSignature,
  setRequiresAdultSignature,
  setRequiresDirectDelivery,
  setUsedImportItemsAiFeature,
  setRequiresEmailNotification,
  addNotificationEmail,
  setNotificationEmails,
  removeNotificationEmail,
  clearNotificationEmails,
  setExtraServiceOptions,
  setDHLValueAddedServices,
  setInsuranceAmount,
  setInsuranceCurrency,
  setDefaultFromAddress,
  SET_DEFAULT_PACKAGE_DIMENSIONS,
  setDuplicateOrEdit,
  setEditingOriginalReferences,
  setKeptDocumentTypes,
  setCurrentQuoteReferenceIsValid,
  setTotalShipmentValue,
  setSendIntegrationUpdate,
  setShipmentDateFromUI,
} = createShipmentSlice.actions;

export default createShipmentSlice.reducer;

// The sender in state carries fields the drop-off endpoints do not accept
// (uuid, updateAddressBook, ...), and their DTO is validated with
// forbidNonWhitelisted, so pick the address fields explicitly.
const toDropOffAddress = (sender: CreateAddress): DropOffAddress => ({
  addressLine1: sender.addressLine1,
  addressLine2: sender.addressLine2,
  city: sender.city,
  countyState: sender.countyState,
  zip: sender.zip,
  countryIsoCode: sender.countryIsoCode,
  isSenderDefault: sender.isSenderDefault,
});

export const setDropOffThunk = (
  sender: CreateAddress,
  salesAccountUUID: string
): AppThunk => {
  return async (dispatch) => {
    try {
      const answer = await fetchShipmentDropOffLocations(
        toDropOffAddress(sender),
        salesAccountUUID
      );
      const dropOffLocations = answer.data.output.locationDetailList.map(
        (element) => {
          const address = element.contactAndAddress.address;
          const distance = `${element.distance.value} ${element.distance.units}`;
          const carrierDetailList = element.carrierDetailList;
          const locationDetail = element;
          return { address, distance, carrierDetailList, locationDetail };
        }
      );

      dispatch(setDropOffLocations(dropOffLocations));
    } catch (error) {
      console.error("Error fetching drop-off locations:", error);
    }
  };
};
export const setDropOffThunkUPS = (
  sender: CreateAddress,
  serviceId: string,
  salesAccountUUID: string
): AppThunk => {
  return async (dispatch) => {
    try {
      const payload: DropOffAddressWithServiceId = {
        ...toDropOffAddress(sender),
        serviceId,
      };
      const answer = await fetchShipmentDropOffLocationsUPS(
        payload,
        salesAccountUUID
      );
      const dropOffLocations =
        answer.data?.locatorResponse?.searchResults?.dropLocation?.map(
          (element) => {
            const address = {
              city: element.addressKeyFormat.politicalDivision2,
              countryCode: element.addressKeyFormat.countryCode,
              postalCode: element.addressKeyFormat.postcodePrimaryLow,
              residential: false,
              stateOrProvinceCode: element.addressKeyFormat.politicalDivision1,
              displayName: element.addressKeyFormat.consigneeName,
            };
            const distance = `${element.distance.value} ${element.distance.unitOfMeasurement.code}`;
            const geoLocation = {
              latitude: element.geocode.latitude,
              longitude: element.geocode.longitude,
            };
            const phone = element.phoneNumber;
            const storeName =
              element.locationAttribute[0].optionCode.description;
            return { address, distance, geoLocation, phone, storeName };
          }
        ) || [];
      dispatch(setDropOffLocations(dropOffLocations));
      if (dropOffLocations.length) {
        dispatch(setSelectedLocation(dropOffLocations[0].address));
      }
    } catch (error) {
      console.error("Error fetching drop-off locations:", error);
    }
  };
};

// DHL's half of the "Closest drop off locations" picker. Deliberately maps into the same
// shape setDropOffThunkUPS builds, so one DropOffSelect renders both carriers.
export const setDropOffThunkDHL = (
  sender: CreateAddress,
  rateId: IQuotedRate["id"],
  salesAccountUUID: ISalesAccount["uuid"]
): AppThunk => {
  return async (dispatch) => {
    try {
      const answer = await fetchDHLServicePoints(
        salesAccountUUID,
        rateId,
        toDropOffAddress(sender)
      );
      const dropOffLocations =
        answer.data?.servicePoints?.map((point: DHLServicePoint) => {
          const contact = Array.isArray(point.contactDetails)
            ? point.contactDetails[0]
            : point.contactDetails;

          const address = {
            city: point.address?.city ?? "",
            countryCode: point.address?.country ?? "",
            postalCode: point.address?.zipCode ?? "",
            residential: false,
            stateOrProvinceCode: point.address?.state ?? "",
            // The shop's own name is what someone looks for on the street; DHL's
            // servicePointName is the generic "DHL Service Point" and goes below it,
            // where UPS shows the kind of location.
            displayName: point.localName || point.servicePointName || "",
          };

          return {
            address,
            distance: point.distance ?? "",
            geoLocation:
              point.geoLocation?.latitude != null &&
              point.geoLocation?.longitude != null
                ? {
                    latitude: String(point.geoLocation.latitude),
                    longitude: String(point.geoLocation.longitude),
                  }
                : undefined,
            phone: contact?.phoneNumber,
            storeName: point.servicePointName,
          };
        }) || [];

      dispatch(setDropOffLocations(dropOffLocations));
      if (dropOffLocations.length) {
        dispatch(setSelectedLocation(dropOffLocations[0].address));
      }
    } catch (error) {
      console.error("Error fetching DHL drop-off locations:", error);
    }
  };
};

export const setDHLPickupCapabilitiesThunk = (
  salesAccountUUID: ISalesAccount["uuid"],
  rateId: IQuotedRate["id"]
): AppThunk => {
  return async (dispatch) => {
    try {
      const answer = await fetchDHLPickupCapabilities(
        salesAccountUUID,
        rateId
      );
      dispatch(
        setDHLPickupCapabilities(answer.data?.pickupCapabilities ?? null)
      );
    } catch (error) {
      console.error("Error fetching DHL pickup capabilities:", error);
    }
  };
};
